import { ref, onMounted, onBeforeUnmount } from 'vue'
import { dequeueAll, removeEdit, countPending, type OfflineEdit } from './useOfflineQueue'
import { diaryApi } from '@api/services/diary'
import { bodyMetricApi } from '@api/services/bodymetric'
import { todoApi } from '@api/services/todo'
import { anniversaryApi } from '@api/services/anniversary'
import { strengthApi } from '@api/services/strength'

export type SyncResult = 'success' | 'conflict' | 'error'

/** Conflict resolution policy:
 * - server-wins: discard local edit on 409 (safest, default)
 * - client-wins: force apply local edit with If-Match bypass (future) */
export type ConflictPolicy = 'server-wins' | 'client-wins'

const isOnline = ref(typeof navigator !== 'undefined' ? navigator.onLine : true)
const pendingCount = ref(0)
const isSyncing = ref(false)
const lastConflict = ref<string | null>(null)
const conflictPolicy = ref<ConflictPolicy>('server-wins')

async function refreshCount() {
  pendingCount.value = await countPending()
}

async function applyEdit(edit: OfflineEdit): Promise<SyncResult> {
  try {
    switch (edit.module) {
      case 'diary':
        await diaryApi.createDiary({ diaryCreateRequest: edit.payload as never })
        break
      case 'bodymetric':
        await bodyMetricApi.createBodyMetric({ bodyMetricCreateRequest: edit.payload as never })
        break
      case 'todo':
        if (edit.action === 'create') {
          await todoApi.createTodo({ todoCreateRequest: edit.payload as never })
        } else if (edit.action === 'update' && edit.serverId) {
          await todoApi.updateTodo({ id: edit.serverId, todoUpdateRequest: edit.payload as never })
        } else if (edit.action === 'delete' && edit.serverId) {
          await todoApi.deleteTodo({ id: edit.serverId })
        }
        break
      case 'anniversary':
        if (edit.action === 'create') {
          await anniversaryApi.createAnniversary({ anniversaryCreateRequest: edit.payload as never })
        } else if (edit.action === 'update' && edit.serverId) {
          await anniversaryApi.updateAnniversary({ id: edit.serverId, anniversaryUpdateRequest: edit.payload as never })
        } else if (edit.action === 'delete' && edit.serverId) {
          await anniversaryApi.deleteAnniversary({ id: edit.serverId })
        }
        break
      case 'strength':
        if (edit.action === 'create') {
          await strengthApi.createStrengthLog({ strengthLogCreateRequest: edit.payload as never })
        } else if (edit.action === 'update' && edit.serverId) {
          await strengthApi.updateStrengthLog({ id: edit.serverId, strengthLogCreateRequest: edit.payload as never })
        } else if (edit.action === 'delete' && edit.serverId) {
          await strengthApi.deleteStrengthLog({ id: edit.serverId })
        }
        break
    }
    return 'success'
  } catch (e: unknown) {
    const err = e as { response?: { status: number } }
    if (err.response?.status === 409) return 'conflict'
    return 'error'
  }
}

async function handleConflict(edit: OfflineEdit): Promise<void> {
  lastConflict.value = `${edit.module} ${edit.action} @ ${new Date(edit.timestamp).toLocaleTimeString()}`
}

export async function flushQueue(): Promise<{ synced: number; conflicts: number; errors: number }> {
  if (isSyncing.value || !isOnline.value) return { synced: 0, conflicts: 0, errors: 0 }
  isSyncing.value = true
  const edits = await dequeueAll()
  let synced = 0
  let conflicts = 0
  let errors = 0

  for (const edit of edits) {
    const result = await applyEdit(edit)
    if (result === 'success') {
      synced++
      await removeEdit(edit.id)
    } else if (result === 'conflict') {
      conflicts++
      await handleConflict(edit)
      await removeEdit(edit.id)
    } else {
      errors++
      break
    }
  }

  isSyncing.value = false
  await refreshCount()
  return { synced, conflicts, errors }
}

export function useOfflineSync() {
  function onOnline() {
    isOnline.value = true
    flushQueue()
  }

  function onOffline() {
    isOnline.value = false
  }

  onMounted(() => {
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    refreshCount()
    if (isOnline.value && pendingCount.value > 0) {
      flushQueue()
    }
  })

  onBeforeUnmount(() => {
    window.removeEventListener('online', onOnline)
    window.removeEventListener('offline', onOffline)
  })

  return {
    isOnline,
    pendingCount,
    isSyncing,
    lastConflict,
    conflictPolicy,
    flushQueue,
    refreshCount
  }
}
